import { GetStaticPaths, GetStaticProps, GetStaticPropsResult } from 'next'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { Seo } from '@/layout/Seo'
import { HideTopNavigationLayout, NextPageWithLayout } from '@/layouts'
import { CatalogSeason, CatalogSeries, CatalogTitle, fetchTitle, isEpisode } from '@/services/ContentCatalog'
import { getShareLink, ShareLink } from '@/services/ContentShareService'
import { ProjectMeta } from '@/services/ProjectsService'
import { getLocaleFromParams } from '@/utils/LocaleUtil'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getStaticPropsErrorHandler, getUrlFromThisFilePath } from '@/utils/nextUtils/nextErrorHandlers'
import { omitUndefineds } from '@/utils/object'
import { getBasePath } from '@/utils/sitemap/base'
import { loadTranslations } from '@/utils/translate/translate-server'
import { PerfLogsProvider } from '@/utils/usePerfLogs'
import { ContentSharedViewDisplayFields, OneToManyContentSharedView } from '@/views/ContentSharedView'

interface OneToManyContentSharedPageProps {
  id: string
  locale: string
  shareLink: ShareLink
  watchableTitle: CatalogTitle
  displayFields: ContentSharedViewDisplayFields
}

// NOTE This page is for 1:many sharing, and is a very different experience than 1:1 sharing. See /watch/share for 1:1
export const OneToManyContentSharedPage: NextPageWithLayout<OneToManyContentSharedPageProps & ProjectMeta> = (
  props,
) => {
  const { shareLink, id, locale, watchableTitle, displayFields } = props
  const path = `${paths.watch.index}/shared/${id}`
  const url = `${getBasePath(locale)}${path}`

  const metadata = shareLink.metadata
  const description = metadata.opengraph_description
  const title = metadata.opengraph_title
  const images = [
    {
      url: metadata.opengraph_image,
      width: 1200,
      alt: description,
    },
  ]

  return (
    <>
      <Seo
        canonical={url}
        description={description}
        openGraph={{ title, description, url, images }}
        path={path}
        title={title}
        nofollow
        noindex
      />
      <PerfLogsProvider>
        <OneToManyContentSharedView
          shareLink={shareLink}
          watchableTitle={watchableTitle}
          displayFields={displayFields}
        />
      </PerfLogsProvider>
    </>
  )
}

OneToManyContentSharedPage.getLayout = (page) => {
  return <HideTopNavigationLayout isDarkMode={true}>{page}</HideTopNavigationLayout>
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps<OneToManyContentSharedPageProps> = async ({ params = {} }) =>
  getStaticPropsErrorHandler<OneToManyContentSharedPageProps>(
    { requestUrl: getUrlFromThisFilePath(params) },
    async (): Promise<GetStaticPropsResult<OneToManyContentSharedPageProps>> => {
      const locale = getLocaleFromParams(params)

      const id = getItemOrFirstEntry(params.id)
      if (!id) {
        return {
          redirect: {
            destination: paths.watch.index,
            permanent: false,
          },
        }
      }

      const shareLink = await getShareLink({ id })
      if (!shareLink) {
        return { notFound: true, revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS }
      }

      const raw = await fetchTitle<CatalogTitle>(locale, shareLink.content_id)
      const watchableTitle = omitUndefineds(raw)

      if (!watchableTitle) {
        return { notFound: true, revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS }
      }

      const displayFields = await getDisplayFields(locale, watchableTitle)

      return {
        props: {
          displayFields,
          shareLink,
          locale,
          id,
          watchableTitle,
          ...(await loadTranslations(locale, ['common', 'watch'])),
        },
      }
    },
  )

async function getDisplayFields(locale: string, watchableTitle: CatalogTitle): Promise<ContentSharedViewDisplayFields> {
  if (isEpisode(watchableTitle)) {
    const episodeTitle = watchableTitle
    const seasonTitle = await fetchTitle<CatalogSeason>(locale, episodeTitle.seasonId)
    const seriesTitle = seasonTitle?.seriesId
      ? await fetchTitle<CatalogSeries>(locale, seasonTitle.seriesId)
      : undefined

    const subTitle =
      typeof seasonTitle?.number === 'number' && typeof episodeTitle?.number === 'number' && seriesTitle?.title
        ? `S${seasonTitle.number}:E${episodeTitle.number} ${seriesTitle?.title}`
        : undefined

    return { title: episodeTitle.title, subTitle }
  }
  return { title: watchableTitle.title }
}

export default OneToManyContentSharedPage
